/**
 * Tilt-to-steer using DeviceOrientationEvent. Opt-in via settings.
 *
 * iOS 13+ gates DeviceOrientation behind a permission prompt that must be
 * triggered from a user gesture. Android and desktop don't gate it. We
 * handle both paths transparently and surface a single boolean state.
 *
 * Drives the same `Input.externalVec` channel the virtual joystick uses,
 * so the existing Game / Ship integration is untouched.
 */

const DEADZONE_DEG = 4;
const FULL_TILT_DEG = 28; // tilting past this gives max lateral input.
const NEUTRAL_BETA = 60;  // landscape-held phone reads ~60° beta at rest.

type Setter = (v: { x: number; y: number } | null) => void;

export class Gyro {
  private listening = false;
  private setExternal: Setter;
  private handler = (e: DeviceOrientationEvent) => this.onOrient(e);

  constructor(setExternal: Setter) {
    this.setExternal = setExternal;
  }

  /**
   * Enable gyro input. On iOS this triggers the permission prompt — caller
   * MUST invoke this from inside a user gesture or iOS rejects silently.
   * Returns true if the listener attached, false if permission denied.
   */
  async enable(): Promise<boolean> {
    if (this.listening) return true;
    const D = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<"granted" | "denied">;
    };
    if (typeof D.requestPermission === "function") {
      try {
        const result = await D.requestPermission();
        if (result !== "granted") return false;
      } catch {
        return false;
      }
    }
    window.addEventListener("deviceorientation", this.handler);
    this.listening = true;
    return true;
  }

  disable() {
    if (!this.listening) return;
    window.removeEventListener("deviceorientation", this.handler);
    this.listening = false;
    this.setExternal(null);
  }

  private onOrient(e: DeviceOrientationEvent) {
    const gamma = e.gamma ?? 0; // roll, -90..90
    const beta = e.beta ?? 90;  // pitch, -180..180 (landscape neutral ≈ 60-90)

    const x = applyDeadzone(gamma) / FULL_TILT_DEG;
    const y = applyDeadzone(beta - NEUTRAL_BETA) / FULL_TILT_DEG;
    const clamped = {
      x: clamp(x, -1, 1),
      y: clamp(-y, -1, 1), // invert: tilting top of phone forward = ship down
    };
    if (clamped.x === 0 && clamped.y === 0) {
      this.setExternal(null);
    } else {
      this.setExternal(clamped);
    }
  }
}

function applyDeadzone(deg: number): number {
  if (Math.abs(deg) < DEADZONE_DEG) return 0;
  return deg - Math.sign(deg) * DEADZONE_DEG;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}
